/**
 * AddHotelForm Component
 * 
 * A comprehensive form component used for both adding new hotel accommodations and
 * editing existing ones. The mode of operation (add vs. edit) is determined by the
 * presence and content of the `initialData` prop. When `initialData` is provided,
 * the form pre-populates with existing details for editing.
 * 
 * It provides a rich interface for entering hotel information, including:
 * - Name, address, phone, URL
 * - City selection, utilizing the shared `PlacesAutocomplete` component for suggestions.
 *   It can handle standard pre-defined cities and custom city inputs.
 * - Dynamic title generation (e.g., "Hotel Name: City") based on hotel name and city.
 * - Check-in and check-out date/time selection with validation.
 * - Cost tracking with multiple currency support and payment status.
 * - Booking details management (agent, URL, reference).
 * - Notes and status management.
 * 
 * The component manages its own complex state for form data, validation errors,
 * and UI interactions. It relies on `initialData` to populate fields for editing
 * and transforms this data (e.g., date strings to Date objects, city data to a
 * standardized format) for internal use.
 * 
 * Props:
 * @prop {Function} onSubmit - Callback function to handle form submission with compiled hotel data.
 * @prop {Object} [initialData={}] - Pre-existing hotel data. If empty, form is in "add" mode.
 *                                  If populated, form is in "edit" mode.
 * @prop {Function} onCancel - Callback function to handle form cancellation.
 * 
 * State Management:
 * - Manages complex nested form data (e.g., for cost).
 * - Handles validation errors and displays them.
 * - Tracks city selection state, accommodating both Google Places selections and custom entries.
 * - Maintains form completion status and dynamically updates a descriptive title.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { accommodationService } from '../../services/accommodationService';
import { ACCOMMODATION_TYPES, ACCOMMODATION_STATUSES } from '../../constants/accommodationTypes';
import { useAuth, PlacesAutocomplete, PlaceDetails, getPlacesService } from '@trip-planner-monorepo/shared';
import './AddHotelForm.css';
import { useParams } from 'react-router-dom';

const PLACE_AUTOCOMPLETE_COUNTRIES_RESTRICTED = ['US', 'CA', 'GB', 'AU']; // Renamed for clarity
const PLACE_AUTOCOMPLETE_COUNTRIES_GLOBAL = []; // Stable empty array for global search

const AddHotelForm = ({ onSubmit, initialData = {}, onCancel }) => {
  const {  tripId } = useParams();
  const { currentUser } = useAuth();

  const [errors, setErrors] = useState({});
  const [hotelTitle, setHotelTitle] = useState('');

  /**
     * Data Processing for Initial Form State
     * 
     * Transforms raw initial data into form-compatible format:
     * - Converts date strings to Date objects
     * - Normalizes city data structure
     * - Handles field name differences
     */
  /**
   * Process initial data dates - separate date and time components
   */
  const processInitialDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: null, time: null };
    
    const dateTime = new Date(dateTimeString);
    if (isNaN(dateTime.getTime())) return { date: null, time: null };
    
    // Create date-only object (midnight local time)
    const dateOnly = new Date(dateTime.getFullYear(), dateTime.getMonth(), dateTime.getDate());
    
    // Check if original has time component (not midnight)
    const hasTime = dateTime.getHours() !== 0 || dateTime.getMinutes() !== 0 || dateTime.getSeconds() !== 0;
    const timeOnly = hasTime ? dateTime : null;
    
    return { date: dateOnly, time: timeOnly };
  };

  const checkInProcessed = processInitialDateTime(initialData.checkInDate);
  const checkOutProcessed = processInitialDateTime(initialData.checkOutDate);

  const processedInitialData = {
    ...initialData,
    checkIn: checkInProcessed.date,
    checkOut: checkOutProcessed.date,
    checkInTime: checkInProcessed.time,
    checkOutTime: checkOutProcessed.time,
    // City processing will be handled in the state initializer to ensure consistency
  };

  /**
     * City Data Transformation
     * 
     * Converts city string to standardized city object format.
     * Handles both standard and custom cities.
     * 
     * @param {string|Object} cityInput - City name string or existing city object
     * @returns {Object|string} Standardized city object or empty string if input is falsy
     */
  const transformCityInputToStateObject = (cityInput) => {
    if (!cityInput) return ''; // Represents no city

    if (typeof cityInput === 'object') {
      // If it's already an object, ensure it has a 'name' property, else try to adapt
      if (cityInput.name) return cityInput;
      if (cityInput.label) return { ...cityInput, name: cityInput.label }; // Adapt from label if name is missing
      return ''; // Cannot determine a name
    }

    if (typeof cityInput === 'string') {
      // For a simple string, treat it as a custom city name
      return {
        name: cityInput,
        formattedAddress: cityInput, // Default formatted address to name for custom
        isCustom: true,
        source: 'custom' // Indicate it's a custom string input
        // Add other fields like placeId: null, coordinates: null if needed for consistency
      };
    }
    return ''; // Fallback for other types
  };
    
  const initialCityState = transformCityInputToStateObject(initialData.city);

  /**
     * Form State Management
     * 
     * Maintains complete form state with nested objects for:
     * - Basic hotel information
     * - Location details
     * - Booking information
     * - Cost tracking
     * - Status management
     */
  const [formData, setFormData] = useState({
    name: '',
    city: initialCityState, // Initialize with processed city
    address: '', // Stays as a string, will hold formatted address or custom input
    phone: '',
    url: '',
    checkIn: null,
    checkOut: null,
    checkInTime: null,  // Separate time selection
    checkOutTime: null, // Separate time selection
    roomType: '',
    bookingAgent: '',
    bookingUrl: '',
    bookingReference: '',
    notes: '',
    cost: {
      amount: '',
      currency: 'USD',
      paid: false
    },
    status: 'Planned',
    ...processedInitialData,
    city: initialCityState,
    address: processedInitialData.address || '', // Ensure address from initialData is used
  });
  const latestCityInputRef = useRef(
    (initialCityState && typeof initialCityState === 'object')
      ? (initialCityState.formattedAddress || initialCityState.name || '')
      : (initialCityState || '')
  );

  /**
     * Title Generation Effect
     * 
     * Automatically updates the hotel title when name or city changes.
     * Creates a human-readable title for display and storage.
     */
  useEffect(() => {
    updateTitle();
  }, [formData.name, formData.city]);

  /**
     * Title Update Logic
     * 
     * Generates a descriptive title based on available hotel information.
     * Prioritizes combinations of hotel name and city.
     */
  const updateTitle = () => {
    let title = '';
    const name = formData.name;
        
    let cityNameForTitle = '';
    if (formData.city && typeof formData.city === 'object') {
      if (formData.city.source === 'google' && formData.city.formattedAddress) {
        cityNameForTitle = formData.city.formattedAddress;
      } else {
        cityNameForTitle = formData.city.name; 
      }
    } else if (typeof formData.city === 'string') {
      cityNameForTitle = formData.city;
    }
        
    if (name && cityNameForTitle) {
      title = `${name}: ${cityNameForTitle}`;
    } else if (name) {
      title = name;
    } else if (cityNameForTitle) {
      title = `Accommodation in ${cityNameForTitle}`;
    }
        
    setHotelTitle(title);
  };

  /**
     * City Name Extraction for Display
     * 
     * Extracts displayable city name from city data structure.
     * This is primarily for the PlacesAutocomplete initialValue and potentially other UI displays.
     * 
     * @param {Object|string} cityData - City information from formData.city
     * @returns {string} Human-readable city name
     */
  const getDisplayCityName = (cityData) => {
    if (!cityData) return '';
    if (typeof cityData === 'object') {
      // Prefer formattedAddress for display if available (especially from Google)
      if (cityData.source === 'google' && cityData.formattedAddress) {
        return cityData.formattedAddress;
      }
      return cityData.name || ''; // Fallback to name or empty string
    }
    if (typeof cityData === 'string') return cityData; // Fallback if cityData is a simple string
    return '';
  };

  /**
     * Form Input Handler
     * 
     * Manages updates to form state for all field types.
     * Handles nested objects (e.g., cost) and flat fields.
     * 
     * @param {string} fieldName - Name of the field to update
     * @param {any} value - New value for the field
     */
  const handleInputChange = (fieldName, value) => {
    if (fieldName.startsWith('cost.')) {
      const costField = fieldName.split('.')[1];
      setFormData({
        ...formData,
        cost: {
          ...formData.cost,
          [costField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [fieldName]: value
      });
    }

    // Clear error for this field when it's changed
    if (errors[fieldName]) {
      setErrors({
        ...errors,
        [fieldName]: null
      });
    }
  };

  /**
     * City Selection Handler
     * 
     * Manages updates to the selected city.
     * Handles both standard and custom cities.
     * 
     * @param {Object} city - Selected city object
     */
  const handleCityChange = useCallback((city) => {
    console.log('AddHotelForm: handleCityChange called with city:', JSON.stringify(city));
    setFormData(prevFormData => {
      console.log('AddHotelForm: setFormData in handleCityChange. New city:', JSON.stringify(city), 'Previous city:', JSON.stringify(prevFormData.city));
      const newCityState = typeof city === 'string' ? { name: city, formattedAddress: city, isCustom: true } : city;
      return {
        ...prevFormData,
        city: newCityState // Ensure city is stored as an object or ''
      };
    });
    // Clear related errors if any
    if (errors.city) {
      setErrors(prevErrors => ({ ...prevErrors, city: null }));
    }
    if (errors.form && (formData.name || getDisplayCityName(city))) { // Check if form-level error can be cleared
      setErrors(prevErrors => ({ ...prevErrors, form: null }));
    }
  }, [errors, formData.name]); // Added dependencies: errors, formData.name. Consider if setFormData is needed as dep.

  /**
     * Handler for when a place is selected from Google Places Autocomplete.
     * @param {PlaceDetails} placeDetails - The details object from PlacesAutocomplete component.
     */
  const handlePlaceSelected = (placeDetails) => {
    console.log('AddHotelForm: Google Place selected:', placeDetails);
    let cityName = placeDetails.name; // Default to place name
    // If placeType was '(cities)', the 'name' might be the establishment, locality is better.
    if (placeDetails.addressComponents?.locality) {
      cityName = placeDetails.addressComponents.locality;
    }

    const newCityObject = {
      name: cityName,
      formattedAddress: placeDetails.formattedAddress || cityName,
      placeId: placeDetails.placeId || '',
      coordinates: placeDetails.coordinates || null,
      addressComponents: placeDetails.addressComponents || null,
      isCustom: false,
      source: 'google'
    };
    latestCityInputRef.current = newCityObject.formattedAddress || newCityObject.name || '';
    setFormData(prevFormData => ({
      ...prevFormData,
      city: newCityObject
    }));
    // Clear related errors
    if (errors.city) setErrors(prevErrors => ({ ...prevErrors, city: null }));
    if (errors.form && (formData.name || cityName)) setErrors(prevErrors => ({ ...prevErrors, form: null }));
  };

  /**
     * Handler for when custom input is provided in PlacesAutocomplete.
     * @param {string} customInputValue - The custom string input by the user.
     */
  const handleCustomCityInput = (customInputValue) => {
    console.log('AddHotelForm: Custom city input:', customInputValue);
    if (!customInputValue.trim()) { // If input is empty or just whitespace, treat as no city
      latestCityInputRef.current = '';
      setFormData(prevFormData => ({
        ...prevFormData,
        city: '' 
      }));
      // Potentially trigger validation if name is also empty
      if (!formData.name && errors.form) {
        // errors.form = 'At least Hotel Name or City is required.';
      }
      return;
    }
    const newCityObject = {
      name: customInputValue,
      formattedAddress: customInputValue, // Or set to empty/null if only name is "known"
      isCustom: true,
      source: 'custom'
    };
    latestCityInputRef.current = customInputValue;
    setFormData(prevFormData => ({
      ...prevFormData,
      city: newCityObject
    }));
    // Clear related errors
    if (errors.city) setErrors(prevErrors => ({ ...prevErrors, city: null }));
    if (errors.form && (formData.name || customInputValue)) setErrors(prevErrors => ({ ...prevErrors, form: null }));
  };

  /**
     * Handler for when a specific address is selected from Google Places Autocomplete for the Hotel Address field.
     * Attempts to find a matching Google Place for the extracted city to ensure data consistency.
     * @param {PlaceDetails} addressPlaceDetails - The details object from PlacesAutocomplete component for the address.
     */
  const handleAddressSelected = async (addressPlaceDetails) => {
    console.log('AddHotelForm: Google Place selected for address:', addressPlaceDetails);
    const newAddress = addressPlaceDetails.formattedAddress || addressPlaceDetails.name || '';

    let cityToUpdate = formData.city; // Default to current city state if no new city is derived

    const extractedLocality = addressPlaceDetails.addressComponents?.locality
      || addressPlaceDetails.addressComponents?.administrativeArea;
    const extractedAdminArea = addressPlaceDetails.addressComponents?.administrativeArea;
    const extractedCountry = addressPlaceDetails.addressComponents?.country;

    if (extractedLocality) {
      cityToUpdate = {
        name: extractedLocality,
        formattedAddress: extractedLocality,
        placeId: '',
        coordinates: addressPlaceDetails.coordinates || null,
        addressComponents: {
          locality: addressPlaceDetails.addressComponents?.locality || extractedLocality,
          country: extractedCountry || '',
          administrativeArea: extractedAdminArea || '',
        },
        isCustom: false,
        source: 'google-address-derived'
      };
      latestCityInputRef.current = extractedLocality;

      // Persist address + city before the extra city lookup so Save is not racing it.
      setFormData(prevFormData => ({
        ...prevFormData,
        address: newAddress,
        city: cityToUpdate
      }));

      try {
        let cityQuery = extractedLocality;
        if (extractedAdminArea) cityQuery += `, ${extractedAdminArea}`;
        if (extractedCountry) cityQuery += `, ${extractedCountry}`;

        console.log(`AddHotelForm: Querying for city: ${cityQuery}`);

        const foundPlaces = await getPlacesService().searchPlacesByText(cityQuery);

        let matchedCityPlace = null;
        if (foundPlaces && foundPlaces.length > 0) {
          const candidate = foundPlaces[0];
          const isLikelyCity = candidate.types?.includes('locality') ||
            candidate.types?.includes('administrative_area_level_1') ||
            candidate.types?.includes('postal_town');
          const cityTypes = ['locality', 'postal_town', 'administrative_area_level_3', 'administrative_area_level_2', 'administrative_area_level_1'];

          if (isLikelyCity && candidate.placeId && candidate.types?.some((t) => cityTypes.includes(t))) {
            matchedCityPlace = candidate;
          }
        }

        if (matchedCityPlace) {
          console.log('AddHotelForm: Matched city via Places API:', matchedCityPlace);
          const mappedCityAddressComponents = {
            locality: matchedCityPlace.addressComponents?.locality || matchedCityPlace.name || '',
            country: matchedCityPlace.addressComponents?.country || '',
            administrativeArea: matchedCityPlace.addressComponents?.administrativeArea || '',
            postalCode: matchedCityPlace.addressComponents?.postalCode || '',
          };

          const cityMainName = mappedCityAddressComponents.locality;
          let displayAddressForCity = matchedCityPlace.formattedAddress;

          if (!displayAddressForCity && cityMainName) {
            displayAddressForCity = cityMainName;
          } else if (!displayAddressForCity && !cityMainName) {
            displayAddressForCity = '';
          }

          const countryName = mappedCityAddressComponents.country;

          if (countryName && displayAddressForCity &&
                        displayAddressForCity.toLowerCase().indexOf(countryName.toLowerCase()) === -1) {

            if (displayAddressForCity.endsWith(',')) {
              displayAddressForCity += ` ${countryName}`;
            } else {
              displayAddressForCity += `, ${countryName}`;
            }
          }

          cityToUpdate = {
            name: cityMainName,
            formattedAddress: displayAddressForCity,
            placeId: matchedCityPlace.placeId,
            coordinates: matchedCityPlace.coordinates?.lat || matchedCityPlace.coordinates?.lng
              ? matchedCityPlace.coordinates
              : (addressPlaceDetails.coordinates || null),
            addressComponents: mappedCityAddressComponents,
            isCustom: false,
            source: 'google-address-derived-matched'
          };
          latestCityInputRef.current = cityToUpdate.formattedAddress || cityToUpdate.name || extractedLocality;
        } else {
          console.log('AddHotelForm: No definitive Google Place match for derived city. Using extracted string.');
          cityToUpdate = {
            name: extractedLocality,
            formattedAddress: extractedLocality,
            placeId: '',
            coordinates: addressPlaceDetails.coordinates || null,
            addressComponents: {
              locality: extractedLocality,
              country: extractedCountry || '',
              administrativeArea: extractedAdminArea || '',
            },
            isCustom: false, // Still from Google data, just not a full Place object for the city
            source: 'google-address-derived'
          };
        }
      } catch (error) {
        console.error('AddHotelForm: Error trying to match extracted city via Places API:', error);
        cityToUpdate = {
          name: extractedLocality, // Fallback to extracted name
          formattedAddress: extractedLocality,
          placeId: '',
          coordinates: addressPlaceDetails.coordinates || null,
          addressComponents: {
            locality: extractedLocality,
            country: extractedCountry || '',
            administrativeArea: extractedAdminArea || '',
          },
          isCustom: false,
          source: 'google-address-derived-error'
        };
      }
    } else {
      console.log('AddHotelForm: No locality found in address components to derive city from.');
    }

    setFormData(prevFormData => ({
      ...prevFormData,
      address: newAddress,
      city: cityToUpdate
    }));

    if (errors.address) setErrors(prevErrors => ({ ...prevErrors, address: null }));
    if (errors.city && JSON.stringify(cityToUpdate) !== JSON.stringify(formData.city)) { // Compare effectively
      setErrors(prevErrors => ({ ...prevErrors, city: null }));
    }
    if (errors.form && (formData.name || (cityToUpdate && cityToUpdate.name))) {
      setErrors(prevErrors => ({ ...prevErrors, form: null }));
    }
  };

  /**
     * Handler for when custom input is provided in PlacesAutocomplete for the Hotel Address field.
     * @param {string} customInputValue - The custom string input by the user.
     */
  const handleCustomAddressInput = (customInputValue) => {
    console.log('AddHotelForm: Custom address input:', customInputValue);
    setFormData(prevFormData => ({
      ...prevFormData,
      address: customInputValue
    }));
    // Clear related errors
    if (errors.address) setErrors(prevErrors => ({ ...prevErrors, address: null }));
  };

  /**
     * Date Formatting
     * 
     * Converts Date objects to human-readable strings.
     * 
     * @param {Date} date - Date to format
     * @returns {string} Formatted date string
     */
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
     * Validation Logic for Form Fields
     * 
     * Validates all required and conditional fields.
     * Sets specific error messages for each invalid field.
     * 
     * @returns {boolean} True if form is valid, false otherwise
     */
  const validateForm = () => {
    const newErrors = {};
    // Example validation - adapt to your specific needs
    if (!formData.name && !getDisplayCityName(formData.city)) {
      newErrors.form = 'At least Hotel Name or City is required.';
    }
    // Add other field validations here
    // if (!formData.name) newErrors.name = 'Hotel name is required.';
    // if (!getDisplayCityName(formData.city)) newErrors.city = 'City is required.';
        
    if (formData.checkIn && formData.checkOut && formData.checkIn >= formData.checkOut) {
      newErrors.checkOut = 'Check-out date must be after check-in date.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
     * Form Submission Handler
     * 
     * Processes form submission:
     * - Validates form data
     * - Formats data for storage
     * - Handles submission callback
     * 
     * @param {Event} e - Form submission event
     */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      console.log('Form validation failed', errors);
      return;
    }

    let cityDataForSubmission = '';
    if (formData.city && typeof formData.city === 'object') {
      // Prefer formattedAddress if it exists (likely from Google Places selection)
      // and it's different from just the name (e.g. "Paris, France" vs "Paris")
      // Or if source is google, always prefer formattedAddress if available.
      if (formData.city.source === 'google' && formData.city.formattedAddress) {
        cityDataForSubmission = formData.city.formattedAddress;
      } else {
        cityDataForSubmission = formData.city.name; // Fallback to name (e.g. for custom cities or if formattedAddress is missing)
      }
    } else if (typeof formData.city === 'string') {
      cityDataForSubmission = formData.city; // If city is just a string
    }
    if (!cityDataForSubmission && latestCityInputRef.current?.trim()) {
      cityDataForSubmission = latestCityInputRef.current.trim();
    }

    /**
     * Date Processing Helper
     * 
     * Combines separate date and time selections into appropriate format:
     * - Date-only format (YYYY-MM-DD) when no time is selected
     * - Full ISO datetime when time is explicitly selected
     * 
     * @param {Date} date - The date object
     * @param {Date} time - The time object (optional)
     * @returns {string|null} Formatted date string or null
     */
    const formatDateForSubmission = (date, time) => {
      if (!date) return null;
      
      if (!time) {
        // Date only - store as YYYY-MM-DD to avoid timezone issues
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } else {
        // Date + Time - combine them and store as ISO string
        const combinedDateTime = new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          time.getHours(),
          time.getMinutes(),
          time.getSeconds()
        );
        return combinedDateTime.toISOString();
      }
    };

    // Revert to the nested structure expected by AddAccommodation.jsx
    const dataToSubmit = {
      details: {
        name: formData.name || '',
        city: cityDataForSubmission, // City name string
        address: formData.address || '',
        phone: formData.phone || '',
        url: formData.url || '',
        checkInDate: formatDateForSubmission(formData.checkIn, formData.checkInTime),
        checkOutDate: formatDateForSubmission(formData.checkOut, formData.checkOutTime),
        roomType: formData.roomType || '',
        bookingAgent: formData.bookingAgent || '',
        bookingUrl: formData.bookingUrl || '',
        bookingReference: formData.bookingReference || '',
        notes: formData.notes || ''
      },
      title: hotelTitle || (formData.name || `Accommodation in ${cityDataForSubmission}`.trim()),
      accommodationType: ACCOMMODATION_TYPES.HOTEL,
      status: formData.status || 'Planned',
      cost: {
        ...formData.cost,
        amount: formData.cost.amount ? parseFloat(formData.cost.amount) : 0
      },
      // Add tripId and userId if they are expected at the top level by AddAccommodation.jsx
      // or if they should also be within 'details'. Assuming top-level for now based on previous structure.
      tripId: tripId,
      userId: currentUser?.uid,
    };

    // Ensure 'summary' field is included, using the logic from 'title'
    // as AddAccommodation.jsx expects 'summary'.
    dataToSubmit.summary = hotelTitle || 
                             (formData.name || `Accommodation in ${cityDataForSubmission}`.trim()) || 
                             'Accommodation'; // Fallback if all else is empty
        
    // If 'title' is not used by AddAccommodation.jsx, we can remove it to avoid confusion.
    // For now, let's assume it might be used or can be kept for other purposes, unless specified.
    // delete dataToSubmit.title; // Optional: uncomment if title is definitely not needed by parent

    console.log('Submitting hotel data (with details and summary):', dataToSubmit);
    onSubmit(dataToSubmit);
  };

  /**
     * Form Cancellation Handler
     * 
     * Triggers the cancel callback to close the form.
     */
  const handleCancel = () => {
    onCancel();
  };

  /**
     * Error Management Functions
     * 
     * Handle the display and tracking of form validation errors.
     */
  const getFieldError = (fieldName) => {
    return errors[fieldName] || null;
  };

  const renderFieldError = (fieldName) => {
    const error = getFieldError(fieldName);
    return error ? <div className="error-message">{error}</div> : null;
  };

  // Form Render Structure
  return (
    <form onSubmit={handleSubmit} className="hotel-form">
      {/* Title Section */}
      <div className="hotel-title">
        <h3>Hotel Details</h3>
        <div className={`hotel-title-display ${!hotelTitle ? 'hotel-title-placeholder' : ''}`}>
          {hotelTitle || 'Enter hotel details to generate title'}
        </div>
      </div>

      {/* Basic Information Section - removed heading */}
      <div className="form-section">
        <div className="form-group">
          <label>Hotel Name</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={getFieldError('name') ? 'error' : ''}
            placeholder="Can be added later if not known yet"
          />
          {renderFieldError('name')}
        </div>
                
        <div className="form-group">
          <label>Hotel Address</label>
          <PlacesAutocomplete
            initialValue={formData.address}
            onPlaceSelect={handleAddressSelected}
            onCustomInput={handleCustomAddressInput}
            placeholder="Enter hotel street address or P.O. Box"
            className="form-control"
            placeType="address"
            allowCustomInput={true}
            countries={PLACE_AUTOCOMPLETE_COUNTRIES_GLOBAL}
          />
          {renderFieldError('address')}
        </div>
                
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="hotel-city">City</label>
            <PlacesAutocomplete
              initialValue={getDisplayCityName(formData.city)}
              onPlaceSelect={handlePlaceSelected}
              onCustomInput={handleCustomCityInput}
              onInputChange={(value) => { latestCityInputRef.current = value; }}
              placeholder="Enter city"
              className="form-control"
              placeType="(cities)"
              allowCustomInput={true}
              countries={PLACE_AUTOCOMPLETE_COUNTRIES_GLOBAL}
            />
            {renderFieldError('city')}
          </div>
                    
          <div className="form-group">
            <label>Phone Number</label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
          </div>
                    
          <div className="form-group">
            <label>Website URL</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Stay Dates</h3>
                
        <div className="form-row">
          <div className="form-group">
            <label>Check-in Date</label>
            <DatePicker
              selected={formData.checkIn}
              onChange={(date) => handleInputChange('checkIn', date)}
              onChangeRaw={(e) => {
                // Allow user to type directly - parse on blur or enter
                const typedValue = e.target.value;
                if (typedValue) {
                  const parsedDate = new Date(typedValue);
                  if (!isNaN(parsedDate.getTime())) {
                    handleInputChange('checkIn', parsedDate);
                  }
                }
              }}
              dateFormat="d MMMM yyyy"
              className={getFieldError('checkIn') ? 'error' : ''}
              placeholderText="Select check-in date or type YYYY-MM-DD"
              isClearable
            />
            {renderFieldError('checkIn')}
          </div>
                    
          <div className="form-group">
            <label>Check-in Time (Optional)</label>
            <DatePicker
              selected={formData.checkInTime}
              onChange={(time) => handleInputChange('checkInTime', time)}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={30}
              timeCaption="Time"
              dateFormat="h:mm aa"
              className={getFieldError('checkInTime') ? 'error' : ''}
              placeholderText="Select time (optional)"
              isClearable
            />
            <div className="field-hint">Optional - leave blank for date-only</div>
            {renderFieldError('checkInTime')}
          </div>
        </div>
        
        <div className="form-row">        
          <div className="form-group">
            <label>Check-out Date</label>
            <DatePicker
              selected={formData.checkOut}
              onChange={(date) => handleInputChange('checkOut', date)}
              onChangeRaw={(e) => {
                // Allow user to type directly - parse on blur or enter
                const typedValue = e.target.value;
                if (typedValue) {
                  const parsedDate = new Date(typedValue);
                  if (!isNaN(parsedDate.getTime())) {
                    handleInputChange('checkOut', parsedDate);
                  }
                }
              }}
              dateFormat="d MMMM yyyy"
              className={getFieldError('checkOut') ? 'error' : ''}
              placeholderText="Select check-out date or type YYYY-MM-DD"
              isClearable
            />
            {renderFieldError('checkOut')}
          </div>
                    
          <div className="form-group">
            <label>Check-out Time (Optional)</label>
            <DatePicker
              selected={formData.checkOutTime}
              onChange={(time) => handleInputChange('checkOutTime', time)}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={30}
              timeCaption="Time"
              dateFormat="h:mm aa"
              className={getFieldError('checkOutTime') ? 'error' : ''}
              placeholderText="Select time (optional)"
              isClearable
            />
            <div className="field-hint">Optional - leave blank for date-only</div>
            {renderFieldError('checkOutTime')}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Booking Details</h3>
                
        <div className="form-row">
          <div className="form-group">
            <label>Room Type</label>
            <input
              type="text"
              value={formData.roomType}
              onChange={(e) => handleInputChange('roomType', e.target.value)}
            />
          </div>
                    
          <div className="form-group">
            <label>Booking Reference</label>
            <input
              type="text"
              value={formData.bookingReference}
              onChange={(e) => handleInputChange('bookingReference', e.target.value)}
            />
          </div>
        </div>
                
        <div className="form-row">
          <div className="form-group">
            <label>Booking Agent</label>
            <input
              type="text"
              value={formData.bookingAgent}
              onChange={(e) => handleInputChange('bookingAgent', e.target.value)}
            />
          </div>
                    
          <div className="form-group">
            <label>Booking URL</label>
            <input
              type="url"
              value={formData.bookingUrl}
              onChange={(e) => handleInputChange('bookingUrl', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Cost & Status</h3>
                
        <div className="form-row">
          <div className="form-group">
            <label>Amount</label>
            <input
              type="number"
              value={formData.cost.amount}
              onChange={(e) => handleInputChange('cost.amount', e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
                    
          <div className="form-group">
            <label>Currency</label>
            <select
              value={formData.cost.currency}
              onChange={(e) => handleInputChange('cost.currency', e.target.value)}
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="JPY">JPY (¥)</option>
              <option value="CAD">CAD ($)</option>
              <option value="AUD">AUD ($)</option>
            </select>
          </div>
                    
          <div className="form-group">
            <label>Paid</label>
            <div className="checkbox-wrapper">
              <input
                type="checkbox"
                checked={formData.cost.paid}
                onChange={(e) => handleInputChange('cost.paid', e.target.checked)}
              />
              <span className="checkbox-label">Yes</span>
            </div>
          </div>
        </div>
                
        <div className="form-group">
          <label>Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            {ACCOMMODATION_STATUSES.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="form-section">
        <h3>Additional Notes</h3>
                
        <div className="form-group">
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Enter any additional notes about this hotel"
            rows={4}
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="form-actions">
        <button type="button" className="cancel-button" onClick={handleCancel}>
                    Cancel
        </button>
        <button type="submit" className="save-button">
                    Save Hotel
        </button>
      </div>
    </form>
  );
};

export default AddHotelForm; 